import { Component, OnInit, OnDestroy, Output, EventEmitter, ChangeDetectorRef, ChangeDetectionStrategy, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GtwElementsBaseUrlsModel } from '../../../shared/_models/gtw-elements-base-urls.model';
import { JsonObjectFactoryService } from '../../../shared/_services/json-object-factory.service';
import { F1120SchM2Service } from './f1120-sch-m2.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subscription } from 'rxjs';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-f1120-sch-m2',
    templateUrl: './f1120-sch-m2.component.html',
    styleUrls: ['./f1120-sch-m2.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class F1120SchM2Component implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  private readonly subs = new Subscription();
  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();
  appService: any = {
		USER_SETTINGS: '',
		SERVER_CONFIG: ''
	};

  // Component Properties
  baseURLs: GtwElementsBaseUrlsModel = {} as GtwElementsBaseUrlsModel;
  globalService: any;
  GENERAL_CONFIG: any;
  filterParams: any = {};
  rowData: any = {};
  
  userSettings: any = this.appService.USER_SETTINGS;
  logged_in_user: string = '';
  defaultsMultipleSelected: any[] = [];
  gridData: any = {};
  sicCodeData: any[] = [];
  yesNoData: any[] = [];
  gpVarianceData: any[] = [];
  countryData: any[] = [];
  sourceData: any[] = [];
  attribMastData: any[] = [];
  
  selectedSourceCode: any = null;
  selectedCountryCode: any = null;
  selectedgpVariances: any = null;
  selectedProrateCOGS: any = null;
  selectedFSC: any = null;
  selectedFinancialCorp: any = null;
  selectedSicCode: any = null;
  isSaveClicked: boolean = false;
  is_locked: any;
  loading: boolean = false;
  crudLoading: boolean = false;
  
  title: string = 'Edit Component Defaults';
  minimise: boolean = false;

  constructor(
    private jsonObjectFactory: JsonObjectFactoryService,
    private f1120SchM2Service: F1120SchM2Service,
    private _snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.userSettings = this.appService.USER_SETTINGS;
    this.logged_in_user = this.userSettings?.user?.sso_id || '';
    this.init();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    header.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || header.globalParams || {};
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal) as GtwElementsBaseUrlsModel;
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.globalService = header;
    this.appService = {
      USER_SETTINGS: header.userSettingsData || {},
      SERVER_CONFIG: header.serverConfigData || {}
    };
    this.userSettings = this.appService.USER_SETTINGS;
    this.logged_in_user = this.userSettings?.user?.sso_id || '';
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
    this.globalService.filterParams = this.filterParams;
    this.cdr.markForCheck();
  }

  init(): void {
    if (this.baseURLs && Object.keys(this.baseURLs).length > 0 && this.globalService) {
      this.getLockStatus();
      this.getCodeMastData();
      this.getAttribMast();
      this.initializeData();
    }
  }

  getLockStatus(): void {
    const lockparams = {
      "action_code": 'n10cpv',
    };
    
    this.jsonObjectFactory.getJSONObj(this.GENERAL_CONFIG.base_url + '/loadJsonObject', lockparams, this.globalService, null, null).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data: any) => {
        this.is_locked = data.jsonObject;
        this.checkLockStatus();
        this.cdr.markForCheck();
      });
  }

  checkLockStatus(): void {
    if (this.is_locked != undefined && this.is_locked != null && this.is_locked[0].LOCK_STATUS == 'Y') {
      this.cancel();
      this._snackBar.open("Tax Return has already been filed for selected Tax Year.", 'Close', {
        duration: 4000,
        panelClass: ['error-snackbar']
      });
      return;
    }
  }

  getAttribMast(): void {
    const params = {
      "action_code": 'bsob9d',
    };
    
    this.jsonObjectFactory.getJSONObj(this.GENERAL_CONFIG.base_url + '/loadJsonObject', params, this.globalService, null, null).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data: any) => {
        if (data.errorMessage.length > 0 && data.errorMessage !== 'null') {
          this._snackBar.open(data.errorMessage, 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        } else {
          this.attribMastData = data.jsonObject;
        }
        this.cdr.markForCheck();
      });
  }

  getCodeMastData(): void {
    const params = {
      "action_code": 'dbty56',
    };
    
    this.jsonObjectFactory.getJSONObj(this.GENERAL_CONFIG.base_url + '/loadJsonObject', params, this.globalService, null, null).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data: any) => {
        if (data.errorMessage.length > 0 && data.errorMessage !== 'null') {
          this._snackBar.open(data.errorMessage, 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        } else {
          this.sicCodeData = data.jsonObject.filter((p: any) => p.CODE_GROUP === 'SIC_CODE');
          this.countryData = data.jsonObject.filter((p: any) => p.CODE_GROUP === 'COUNTRY_CODE');
          this.sourceData = data.jsonObject.filter((p: any) => p.CODE_GROUP === 'SOURCE_CODE');
          this.gpVarianceData = data.jsonObject.filter((p: any) => p.CODE_GROUP === 'GP_VARIANCE');
          this.yesNoData = data.jsonObject.filter((p: any) => p.CODE_GROUP === 'YES_NO');
        }
        this.cdr.markForCheck();
      });
  }

  initializeData(): void {
    // Initialize grid data from global params if available
    if (this.globalService?.globalParams?.gridData) {
      const gridData = this.globalService.globalParams.gridData;
      this.processGridData(gridData);
    }
    this.cdr.markForCheck();
  }

  processGridData(gridData: any[]): void {
    if (gridData != null) {
      const tempGridData = [...gridData];
      this.defaultsMultipleSelected = [];
      
      if (tempGridData != undefined) {
        for (const data of tempGridData) {
          if (data.data != undefined) {
            this.defaultsMultipleSelected.push(data.data);
            
            const selectedData = this.defaultsMultipleSelected[this.defaultsMultipleSelected.length - 1];
            if (selectedData.BASKET_0 != '' || selectedData.COUNTRY_0 != '' || selectedData.PRORATE_COGS_0 != '' ||
                selectedData.GP_VARIANCES_0 != '' || selectedData.SIC_CODES_0 != '' || selectedData.FSE_0 != '' ||
                selectedData.FINANCIAL_CORP_0 != '') {
              selectedData["operation_type"] = "U";
            } else {
              selectedData["operation_type"] = "I";
            }
          }
        }
      }
    }
  }

  removeDefaultsRow(name: any): void {
    const index = this.defaultsMultipleSelected.indexOf(name);
    this.defaultsMultipleSelected.splice(index, 1);
  }

  reset(): void {
    console.log("Need to reset the fields.");
    this.selectedSourceCode = '';
    this.selectedCountryCode = '';
    this.selectedgpVariances = '';
    this.selectedProrateCOGS = '';
    this.selectedFSC = '';
    this.selectedFinancialCorp = '';
    this.selectedSicCode = '';
  }

  save(): void {
    this.isSaveClicked = true;
    
    const insertExist = this.defaultsMultipleSelected.find(item => item.operation_type === 'I');
    
    if (insertExist != undefined) {
      if (this.selectedSourceCode == null || this.selectedCountryCode == null || this.selectedgpVariances == null ||
          this.selectedProrateCOGS == null || this.selectedFSC == null || this.selectedFinancialCorp == null || 
          this.selectedSicCode == null) {
        this.isSaveClicked = false;
        this._snackBar.open("Please select all the Defaults", 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        return;
      }
    } else {
      if (this.selectedSourceCode == null && this.selectedCountryCode == null && this.selectedgpVariances == null &&
          this.selectedProrateCOGS == null && this.selectedFSC == null && this.selectedFinancialCorp == null && 
          this.selectedSicCode == null) {
        this.isSaveClicked = false;
        this._snackBar.open("Please select atleast one Default", 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        return;
      }
    }

    const returnClobSettingsObj: any = {};
    const defaultsChangeDetails: any[] = [];
    
    for (const key in this.defaultsMultipleSelected) {
      const returnObj: any = {};
      const gridDataItem = this.globalService?.globalParams?.gridData?.[key]?.data;
      
      if (gridDataItem) {
        returnObj["object_id"] = gridDataItem.object_id;
        returnObj['ho_leid'] = gridDataItem.HO_LEID;
        returnObj['leid'] = gridDataItem.LEID;
        returnObj['cdr_number'] = gridDataItem.CDR_NO;
        returnObj['reporting_period'] = gridDataItem.REPORTING_PERIOD;
        returnObj['me_code'] = gridDataItem.ME_CODE;
        
        const basketAttrib = this.attribMastData.find(item => item.ATTRIB_NAME === 'BASKET');
        const countryAttrib = this.attribMastData.find(item => item.ATTRIB_NAME === 'COUNTRY');
        const prorateCOGSAttrib = this.attribMastData.find(item => item.ATTRIB_NAME === 'PRORATE_COGS');
        const gpVariancesAttrib = this.attribMastData.find(item => item.ATTRIB_NAME === 'GP_VARIANCES');
        const sicCodesAttrib = this.attribMastData.find(item => item.ATTRIB_NAME === 'SIC_CODES');
        const fseAttrib = this.attribMastData.find(item => item.ATTRIB_NAME === 'FSE');
        const financialCorpAttrib = this.attribMastData.find(item => item.ATTRIB_NAME === 'FINANCIAL_CORP');
        
        if (basketAttrib) returnObj[basketAttrib.ATTRIB_KEY + '_0'] = (this.selectedSourceCode != null) ? this.selectedSourceCode : gridDataItem.SOURCE_KEY;
        if (countryAttrib) returnObj[countryAttrib.ATTRIB_KEY + '_0'] = (this.selectedCountryCode != null) ? this.selectedCountryCode : gridDataItem.COUNTRY_KEY;
        if (prorateCOGSAttrib) returnObj[prorateCOGSAttrib.ATTRIB_KEY + '_0'] = (this.selectedProrateCOGS != null) ? this.selectedProrateCOGS : gridDataItem.COGS_KEY;
        if (gpVariancesAttrib) returnObj[gpVariancesAttrib.ATTRIB_KEY + '_0'] = (this.selectedgpVariances != null) ? this.selectedgpVariances : gridDataItem.GROSS_KEY;
        if (sicCodesAttrib) returnObj[sicCodesAttrib.ATTRIB_KEY + '_0'] = (this.selectedSicCode != null) ? this.selectedSicCode : gridDataItem.SIC_KEY;
        if (fseAttrib) returnObj[fseAttrib.ATTRIB_KEY + '_0'] = (this.selectedFSC != null) ? this.selectedFSC : gridDataItem.FSC_KEY;
        if (financialCorpAttrib) returnObj[financialCorpAttrib.ATTRIB_KEY + '_0'] = (this.selectedFinancialCorp != null) ? this.selectedFinancialCorp : gridDataItem.FINANCIAL_KEY;
        
        returnObj['COMBINATION_KEY'] = this.defaultsMultipleSelected[key].CODE_COMBINATION;
        returnObj['operation_type'] = this.defaultsMultipleSelected[key].operation_type;
        
        defaultsChangeDetails.push(returnObj);
      }
    }

    returnClobSettingsObj['sso_id'] = this.logged_in_user;
    returnClobSettingsObj['screen_key'] = this.globalService.globalParams.screen_key;

    this.sendDetails(returnClobSettingsObj, defaultsChangeDetails, "Defaults has been successfully saved/updated");
  }

  sendDetails(returnClobSettingsObj: any, defaultsChangeDetails: any[], message: string): void {
    this.f1120SchM2Service.saveDefaults(returnClobSettingsObj, defaultsChangeDetails, this.globalService, this.GENERAL_CONFIG).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: any) => {
        if (result.data.errorMessage && result.data.errorMessage !== 'null') {
          this.isSaveClicked = false;
            this._snackBar.open(result.data.errorMessage, 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        } else {
          this._snackBar.open(message, 'Close', {
            duration: 4000,
            panelClass: ['success-snackbar']
          });
          
          // Emit custom event for grid refresh
          const updateEvent = new CustomEvent('request-grid-update', {
            detail: 'refresh',
            bubbles: true
          });
          
          if (document.querySelector('gtw-f1120-sch-m2-ce')) {
            document.querySelector('gtw-f1120-sch-m2-ce')?.dispatchEvent(updateEvent);
          }
        }
        this.cdr.markForCheck();
      });
  }

  cancel(): void {
    // Emit custom event to close modal
    const updateEvent = new CustomEvent('request-grid-update', {
      detail: 'cancel',
      bubbles: true
    });
    
    if (document.querySelector('gtw-f1120-sch-m2-ce')) {
      document.querySelector('gtw-f1120-sch-m2-ce')?.dispatchEvent(updateEvent);
    }
  }

  trackByFn(index: number, item: any): any {
    return item ? item.id : index;
  }
}